import { TranslateService } from '@ngx-translate/core';
import { ParkingRequestClientService } from '@services/api/parking-request/parking-request-client.service';
import { IParkingRequestModel } from '@services/api/parking-request/parking-request-model.interface';
import { LogService } from '@services/util/log.service';
import { MessageService } from 'primeng/api';
import { EventEmitter, inject, Injectable } from '@angular/core';
import { OriginalFormValues, ParkingRequestFormStateService } from './parking-request-form-state.service';
import { CurrentFormValues, ParkingRequestPayloadBuilderService } from './parking-request-payload-builder.service';

/**
 * Interface for component state setters used during form submission
 */
export interface SubmissionStateSetters {
  setSubmitting: (value: boolean) => void;
  setLoading: (value: boolean) => void;
}

/**
 * Interface for component callbacks used during form submission
 */
export interface SubmissionCallbacks {
  loadExistingParkingRequest: (parkingRequest: IParkingRequestModel) => void;
  getCurrentFormValues: () => CurrentFormValues;
  getOriginalValues: () => OriginalFormValues | null;
  isFormValid: () => boolean;
  getParkingRequestId: () => string | null;
}

/**
 * Interface for component event emitters
 */
export interface SubmissionEventEmitters {
  saved: EventEmitter<IParkingRequestModel>;
}

/**
 * Service to handle all form submission operations for parking request forms
 */
@Injectable()
export class ParkingRequestFormSubmissionService {
  private log = inject(LogService);
  private translate = inject(TranslateService);
  private parkingRequestClient = inject(ParkingRequestClientService);
  private payloadBuilderService = inject(ParkingRequestPayloadBuilderService);
  private formStateService = inject(ParkingRequestFormStateService);

  /**
   * Handle form submission
   */
  onSubmit(
    editMode: boolean,
    setters: SubmissionStateSetters,
    callbacks: SubmissionCallbacks,
    eventEmitters: SubmissionEventEmitters,
    messageService: MessageService
  ): void {
    if (!callbacks.isFormValid()) {
      messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.warning'),
        detail: this.translate.instant('parking.form.validationError')
      });
      return;
    }

    setters.setSubmitting(true);

    if (editMode) {
      this.updateParkingRequest(setters, callbacks, eventEmitters, messageService);
    } else {
      this.createParkingRequest(setters, callbacks, eventEmitters, messageService);
    }
  }

  /**
   * Update existing parking request
   */
  private updateParkingRequest(
    setters: SubmissionStateSetters,
    callbacks: SubmissionCallbacks,
    eventEmitters: SubmissionEventEmitters,
    messageService: MessageService
  ): void {
    const originalValues = callbacks.getOriginalValues();
    if (!originalValues) {
      this.log.error('No original values available for update operation');
      setters.setSubmitting(false);
      return;
    }

    const payload = this.payloadBuilderService.buildChangedFieldsPayload(
      callbacks.getCurrentFormValues(),
      originalValues,
      callbacks.getParkingRequestId()
    );

    this.parkingRequestClient.updateItem(payload).subscribe({
      next: (savedRequest) => {
        setters.setSubmitting(false);
        callbacks.loadExistingParkingRequest(savedRequest);
        messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.success'),
          detail: this.translate.instant('parking.form.saveSuccess')
        });
        eventEmitters.saved.emit(savedRequest);
      },
      error: (error) => {
        setters.setSubmitting(false);
        this.log.error('Failed to update parking request', error);
        messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.error'),
          detail: this.translate.instant('parking.form.saveFailed')
        });
      }
    });
  }

  /**
   * Create new parking request
   */
  private createParkingRequest(
    setters: SubmissionStateSetters,
    callbacks: SubmissionCallbacks,
    eventEmitters: SubmissionEventEmitters,
    messageService: MessageService
  ): void {
    const payload = this.payloadBuilderService.buildFullPayload(callbacks.getCurrentFormValues());

    this.parkingRequestClient.createItem(payload).subscribe({
      next: (createdRequest) => {
        setters.setSubmitting(false);
        callbacks.loadExistingParkingRequest(createdRequest);
        messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.success'),
          detail: this.translate.instant('parking.form.createSuccess')
        });
        eventEmitters.saved.emit(createdRequest);
      },
      error: (error) => {
        setters.setSubmitting(false);
        this.log.error('Failed to create parking request', error);
        messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.error'),
          detail: this.translate.instant('parking.form.createFailed')
        });
      }
    });
  }

  /**
   * Validate form before submission
   */
  validateFormForSubmission(callbacks: SubmissionCallbacks, messageService: MessageService): boolean {
    if (!callbacks.isFormValid()) {
      messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.warning'),
        detail: this.translate.instant('parking.form.validationError')
      });
      return false;
    }
    return true;
  }

  /**
   * Handle submission success
   */
  handleSubmissionSuccess(
    savedRequest: IParkingRequestModel,
    isCreate: boolean,
    setters: SubmissionStateSetters,
    callbacks: SubmissionCallbacks,
    eventEmitters: SubmissionEventEmitters,
    messageService: MessageService
  ): void {
    setters.setSubmitting(false);
    callbacks.loadExistingParkingRequest(savedRequest);

    const successMessage = isCreate ? this.translate.instant('parking.form.createSuccess') : this.translate.instant('parking.form.saveSuccess');

    messageService.add({
      severity: 'success',
      summary: this.translate.instant('common.success'),
      detail: successMessage
    });

    eventEmitters.saved.emit(savedRequest);
  }

  /**
   * Handle submission error
   */
  handleSubmissionError(error: any, isCreate: boolean, setters: SubmissionStateSetters, messageService: MessageService): void {
    setters.setSubmitting(false);

    const errorAction = isCreate ? 'create' : 'update';
    this.log.error(`Failed to ${errorAction} parking request`, error);

    const errorMessage = isCreate ? this.translate.instant('parking.form.createFailed') : this.translate.instant('parking.form.saveFailed');

    messageService.add({
      severity: 'error',
      summary: this.translate.instant('common.error'),
      detail: errorMessage
    });
  }
}
